import React, { useState, useEffect } from 'react';
import './TeacherAbsences.css';
import DashboardHeader from '../components/DashboardHeader';
import TeacherNavigationBar from '../components/TeacherNavigationBar';

function TeacherAbsences() {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [absences, setAbsences] = useState({});
  const [loading, setLoading] = useState(false);
  const [showMotifModal, setShowMotifModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [motif, setMotif] = useState('');

  const classes = ['6ème', '5ème', '4ème', '3ème', '2nde', '1ère', 'Terminale'];
  const motifsAbsence = [
    'Maladie',
    'Rendez-vous médical',
    'Problème familial',
    'Transport',
    'Retard',
    'Autre'
  ];

  useEffect(() => {
    if (selectedClass) {
      loadStudents();
    }
  }, [selectedClass, selectedDate]);

  const loadStudents = async () => {
    setLoading(true);
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/admin/etudiants?classe=${selectedClass}&date=${selectedDate}`);
      const data = await response.json();
      
      if (response.ok) {
        setStudents(data.data || data);
        // Initialiser les absences pour chaque étudiant
        const initialAbsences = {};
        (data.data || data).forEach(student => {
          initialAbsences[student.id] = {
            present: true,
            retard: false,
            motif: ''
          };
        });
        setAbsences(initialAbsences);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePresenceChange = (studentId, field, value) => {
    setAbsences(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const handleAbsenceClick = (student) => {
    setSelectedStudent(student);
    setMotif(absences[student.id]?.motif || '');
    setShowMotifModal(true);
  };

  const saveMotif = () => {
    if (selectedStudent) {
      setAbsences(prev => ({
        ...prev,
        [selectedStudent.id]: {
          ...prev[selectedStudent.id],
          motif: motif
        }
      }));
    }
    setShowMotifModal(false);
    setSelectedStudent(null);
    setMotif('');
  };

  const saveAbsences = async () => {
    try {
      const teacherId = localStorage.getItem('teacher_id');
      const teacherMatiere = localStorage.getItem('teacher_matiere') || 'Cours général';
      
      const absencesData = Object.entries(absences)
        .filter(([_, absence]) => !absence.present || absence.retard)
        .map(([studentId, absence]) => ({
          etudiant_id: parseInt(studentId),
          type: absence.present ? 'retard' : 'absent',
          motif: absence.motif || '',
          heure_arrivee: absence.retard ? '08:30' : null // À améliorer avec une vraie saisie
        }));

      if (absencesData.length === 0) {
        alert('Aucune absence ou retard à enregistrer');
        return;
      }

      // Utiliser notre nouvelle API professeur
      const response = await fetch('http://127.0.0.1:8000/api/v1/teacher/absences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enseignant_id: parseInt(teacherId),
          classe: selectedClass,
          date: selectedDate,
          matiere: teacherMatiere,
          absences: absencesData
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`✅ Succès !\n• ${result.data.absences_creees} absence(s) enregistrée(s)\n• ${result.data.notifications_envoyees} notification(s) envoyée(s)\n• Rapport envoyé à l'administration`);
        
        // Réinitialiser le formulaire
        loadStudents();
      } else {
        alert('Erreur: ' + (result.message || 'Erreur inconnue'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  // Supprimer l'ancienne fonction sendNotifications car c'est maintenant géré automatiquement par le backend

  const getStats = () => {
    const total = students.length;
    const presents = Object.values(absences).filter(a => a.present && !a.retard).length;
    const absents = Object.values(absences).filter(a => !a.present).length;
    const retards = Object.values(absences).filter(a => a.retard).length;

    return { total, presents, absents, retards };
  };

  const stats = getStats();

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <TeacherNavigationBar 
        title="Gestion des Absences" 
        onBack={() => window.navigateTo && window.navigateTo('teacher-dashboard')}
      />
      <div className="admin-container">
        <main className="teacher-main admin-content-full">
          <div className="teacher-content">
            <div className="page-header" style={{display: 'none'}}>
              <h1>Gestion des Absences</h1>
            </div>

            {/* Sélection classe et date */}
            <div className="selection-section">
              <div className="selection-row">
                <div className="form-group">
                  <label>Classe</label>
                  <select 
                    value={selectedClass} 
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    <option value="">Sélectionner une classe</option>
                    {Array.isArray(classes) && classes.map(cls => (
                      <option key={typeof cls === 'object' ? cls.id : cls} value={typeof cls === 'object' ? cls.nom : cls}>
                        {typeof cls === 'object' ? cls.nom : cls}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Date</label>
                  <input 
                    type="date" 
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label>&nbsp;</label>
                  <button 
                    className="btn-save"
                    onClick={saveAbsences}
                    disabled={!selectedClass || students.length === 0}
                  >
                    💾 Enregistrer les absences
                  </button>
                </div>
              </div>
            </div>

            {/* Statistiques */}
            {selectedClass && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{stats.total}</div>
                  <div className="stat-label">Total élèves</div>
                </div>
                <div className="stat-card present">
                  <div className="stat-number">{stats.presents}</div>
                  <div className="stat-label">Présents</div>
                </div>
                <div className="stat-card absent">
                  <div className="stat-number">{stats.absents}</div>
                  <div className="stat-label">Absents</div>
                </div>
                <div className="stat-card retard">
                  <div className="stat-number">{stats.retards}</div>
                  <div className="stat-label">Retards</div>
                </div>
              </div>
            )}

            {/* Tableau des présences */}
            {selectedClass && (
              <div className="absences-section">
                <div className="section-header">
                  <h3>Feuille de présence - {selectedClass}</h3>
                </div>

                {loading ? (
                  <div className="loading">Chargement des élèves...</div>
                ) : (
                  <div className="absences-table-container">
                    <table className="absences-table">
                      <thead>
                        <tr>
                          <th>Élève</th>
                          <th>Statut</th>
                          <th>Retard</th>
                          <th>Motif</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map(student => (
                          <tr key={student.id} className={!absences[student.id]?.present ? 'absent-row' : ''}>
                            <td className="student-name">
                              {student.prenom} {student.nom}
                            </td>
                            <td className="status-cell">
                              <div className="status-buttons">
                                <button
                                  className={`status-btn present ${absences[student.id]?.present ? 'active' : ''}`}
                                  onClick={() => handlePresenceChange(student.id, 'present', true)}
                                >
                                  ✓ Présent
                                </button>
                                <button
                                  className={`status-btn absent ${!absences[student.id]?.present ? 'active' : ''}`}
                                  onClick={() => handlePresenceChange(student.id, 'present', false)}
                                >
                                  ✗ Absent
                                </button>
                              </div>
                            </td>
                            <td className="retard-cell">
                              <label className="checkbox-container">
                                <input
                                  type="checkbox"
                                  checked={absences[student.id]?.retard || false}
                                  onChange={(e) => handlePresenceChange(student.id, 'retard', e.target.checked)}
                                  disabled={!absences[student.id]?.present}
                                />
                                <span className="checkmark"></span>
                                {absences[student.id]?.retard && <span className="label-text">Oui</span>}
                              </label>
                            </td>
                            <td className="motif-cell">
                              {(!absences[student.id]?.present || absences[student.id]?.retard) && (
                                <span className="motif-text">
                                  {absences[student.id]?.motif || 'Non spécifié'}
                                </span>
                              )}
                            </td>
                            <td className="actions-cell">
                              {(!absences[student.id]?.present || absences[student.id]?.retard) && (
                                <button 
                                  className="btn-motif"
                                  onClick={() => handleAbsenceClick(student)}
                                >
                                  ✏️ Motif
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Modal pour saisir le motif */}
            {showMotifModal && selectedStudent && (
              <div className="modal-overlay" onClick={() => setShowMotifModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Motif d'absence/retard</h3>
                    <button className="close-btn" onClick={() => setShowMotifModal(false)}>×</button>
                  </div>
                  <div className="modal-body">
                    <p><strong>Élève :</strong> {selectedStudent.prenom} {selectedStudent.nom}</p>
                    <p><strong>Date :</strong> {new Date(selectedDate).toLocaleDateString('fr-FR')}</p>
                    
                    <div className="form-group">
                      <label>Motif</label>
                      <select 
                        value={motif} 
                        onChange={(e) => setMotif(e.target.value)}
                      >
                        <option value="">Sélectionner un motif</option>
                        {motifsAbsence.map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Précisions (optionnel)</label>
                      <textarea
                        value={absences[selectedStudent.id]?.precisions || ''}
                        onChange={(e) => handlePresenceChange(selectedStudent.id, 'precisions', e.target.value)}
                        placeholder="Ajouter des précisions si nécessaire..."
                        rows="3"
                      />
                    </div>
                  </div>
                  <div className="modal-actions">
                    <button className="btn-cancel" onClick={() => setShowMotifModal(false)}>
                      Annuler
                    </button>
                    <button className="btn-confirm" onClick={saveMotif}>
                      Enregistrer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default TeacherAbsences;
